import { Injectable } from "@angular/core";

/**
 * Service to handle logging with constants-based configuration
 */
@Injectable({
  providedIn: "root", // Ensures this service is available globally through dependency injection
})
export class LoggingService {
  private traceLog: string[] = [];

  // Constants for logging configuration
  private debugMode: boolean = true; // Set to true or false based on desired default
  private logToConsole: boolean = true; // Set to true or false based on desired default
  private loggingLevel: "trace" | "warning" | "error" = "trace"; // Default logging level

  private systemIdentifier: string = ""; // To be initialized with relevant identifiers
  private componentLoggingState: Map<number, boolean> = new Map(); // Component-specific logging state
  private nextIdentifier: number = 1; // Counter for unique identifiers

  constructor() {
    this.loadConstantSettings();
  }

  /**
   * Initialize system identifier
   * @param identifier
   */
  initializeSystemIdentifier(identifier: string) {
    this.systemIdentifier = identifier;
  }

  /**
   * Generate a unique identifier for a component
   * @returns Numeric identifier
   */
  getUniqueIdentifier(): number {
    return this.nextIdentifier++;
  }

  /**
   * Enable or disable logging for a specific component by its identifier
   * @param identifier Numeric identifier
   * @param enabled Whether logging is enabled for this component
   */
  setComponentLogging(identifier: number, enabled: boolean) {
    this.componentLoggingState.set(identifier, enabled);
  }

  /**
   * Check if logging is enabled for a specific identifier
   * @param identifier Numeric identifier
   */
  private isComponentLoggingEnabled(identifier: number): boolean {
    return this.componentLoggingState.get(identifier) ?? true; // Default to true
  }

  /**
   * Overloaded method: Log messages
   */
  log(...args: any[]): void;
  log(identifier: number, ...messages: any[]): void;
  log(arg1: any, ...messages: any[]): void {
    if (typeof arg1 === "number") {
      const identifier = arg1;
      if (
        this.isComponentLoggingEnabled(identifier) &&
        this.shouldLog("trace")
      ) {
        if (this.logToConsole) {
          console.log(`[${identifier}]`, ...messages);
        }
        this.addToTraceLog(this.stringifyMessages(...messages));
      }
    } else {
      if (this.shouldLog("trace")) {
        if (this.logToConsole) {
          console.log(...[arg1, ...messages]);
        }
        this.addToTraceLog(this.stringifyMessages(arg1, ...messages));
      }
    }
  }

  warn(...args: any[]): void;
  warn(identifier: number, ...messages: any[]): void;
  warn(arg1: any, ...messages: any[]): void {
    if (typeof arg1 === "number") {
      const identifier = arg1;
      if (
        this.isComponentLoggingEnabled(identifier) &&
        this.shouldLog("warning")
      ) {
        if (this.logToConsole) {
          console.warn(`[${identifier}]`, ...messages);
        }
        this.addToTraceLog(this.stringifyMessages(...messages));
      }
    } else {
      if (this.shouldLog("warning")) {
        if (this.logToConsole) {
          console.warn(...[arg1, ...messages]);
        }
        this.addToTraceLog(this.stringifyMessages(arg1, ...messages));
      }
    }
  }

  error(...args: any[]): void;
  error(identifier: number, ...messages: any[]): void;
  error(arg1: any, ...messages: any[]): void {
    if (typeof arg1 === "number") {
      const identifier = arg1;
      if (
        this.isComponentLoggingEnabled(identifier) &&
        this.shouldLog("error")
      ) {
        if (this.logToConsole) {
          console.error(`[${identifier}]`, ...messages);
        }
        this.addToTraceLog(this.stringifyMessages(...messages), true);
      }
    } else {
      if (this.shouldLog("error")) {
        if (this.logToConsole) {
          console.error(...[arg1, ...messages]);
        }
        this.addToTraceLog(this.stringifyMessages(arg1, ...messages), true);
      }
    }
  }

  /**
   * Check if a message should be logged based on the logging level
   * @param level
   */
  private shouldLog(level: "trace" | "warning" | "error"): boolean {
    const levels = { trace: 1, warning: 2, error: 3 };
    return levels[level] >= levels[this.loggingLevel];
  }

  /**
   * Stringify messages for logging
   * @param messages
   */
  private stringifyMessages(...messages: any[]): string[] {
    return messages.map((msg) => {
      try {
        if (msg === undefined || msg === null) {
          return typeof msg;
        } else if (typeof msg === "function") {
          return `Function: ${msg.name || "anonymous"}`;
        } else if (typeof msg === "object") {
          try {
            return JSON.stringify(msg, null, 1);
          } catch (error) {
            return `Error stringifying object: ${error.message}`;
          }
        } else {
          return msg.toString();
        }
      } catch (ex) {
        return `Error converting message to string: ${ex.message}`;
      }
    });
  }

  /**
   * Internal function to add messages to the trace log
   * @param messages
   * @param uploadToAI
   */
  private addToTraceLog(messages: string[], uploadToAI: boolean = false) {
    const msg = messages.join(", ");
    this.traceLog.push(msg);

    if (uploadToAI) {
      this.uploadToApplicationInsights(`${msg} :: ${this.systemIdentifier}`);
    }
  }

  /**
   * Retrieve all trace log entries
   */
  getTraceLog(): string[] {
    return [...this.traceLog];
  }

  /**
   * Clear all trace log entries
   */
  clearTraceLog() {
    this.traceLog = [];
  }

  /**
   * Upload trace log entries to Application Insights
   */
  uploadTraceLogs() {
    try {
      this.uploadToApplicationInsights(
        `UI Trace Log:\n${this.systemIdentifier}\n${this.traceLog.join("\n")}`
      );
    } catch (error) {
      console.error("Failed to upload trace logs", error);
    }
    this.clearTraceLog();
  }

  /**
   * Simulate Application Insights upload
   * @param message
   */
  private uploadToApplicationInsights(message: string) {
    console.log("Application Insights upload: ", message);
    // Replace with actual Application Insights integration
  }

  /**
   * Load settings from constants
   */
  private loadConstantSettings() {
    // Constants are already set in class properties
  }
}
